/**
 * <atb-pdp-upsell>
 * --------------------------------------------------------------------------
 * Custom replacement for Dawn's `related-products` section on PDPs.
 *
 * - Seeds on the current product (data-product-id).
 * - Fetches /recommendations/products.json with the section's intent
 *   (`related` or `complementary`).
 * - In parallel, fetches the existing `sections/upsell-data.liquid` JSON
 *   endpoint to map handle → short_title metafield. Falls back to full
 *   product.title if no short_title is set.
 * - Lazy-loads via IntersectionObserver (400px rootMargin) so the API
 *   call only fires when the user scrolls near the section.
 * - Quick-add for single-variant available products; "View product"
 *   fallback for multi-variant or fully-sold-out products.
 * - On successful add: opens the cart drawer and publishes the same
 *   PUB_SUB cartUpdate event Dawn uses, so the cart drawer + its
 *   internal upsell module refresh themselves.
 *
 * Architecture mirrors `cart-upsell.js`. Many helpers are deliberately
 * duplicated rather than imported so the module stays self-contained
 * and portable across themes.
 */

class AtbPdpUpsell extends HTMLElement {
  constructor() {
    super();
    this.grid = this.querySelector('.atb-pdp-upsell__grid');
    this.template = this.querySelector('.atb-pdp-upsell__card-template');

    this.productId = this.dataset.productId;
    this.intent = this.dataset.intent || 'related';
    this.limit = parseInt(this.dataset.limit, 10) || 4;

    const colsDesktop = parseInt(this.dataset.columnsDesktop, 10);
    const colsMobile = parseInt(this.dataset.columnsMobile, 10);
    if (Number.isFinite(colsDesktop)) this.style.setProperty('--atb-pdp-columns-desktop', colsDesktop);
    if (Number.isFinite(colsMobile)) this.style.setProperty('--atb-pdp-columns-mobile', colsMobile);

    this.routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this._loaded = false;
  }

  connectedCallback() {
    if (!this.productId || !this.grid || !this.template) {
      this.remove();
      return;
    }

    // Lazy-load: only fetch when the section is near the viewport.
    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._observer.disconnect();
              this.load();
            }
          });
        },
        { rootMargin: '400px 0px' }
      );
      this._observer.observe(this);
    } else {
      // No IntersectionObserver — just load immediately.
      this.load();
    }
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }

  async load() {
    if (this._loaded) return;
    this._loaded = true;

    try {
      const recsUrl = `${this.routeRoot}recommendations/products.json?product_id=${this.productId}&limit=${this.limit}&intent=${this.intent}`;
      const sectionUrl = `${this.routeRoot}recommendations/products?product_id=${this.productId}&limit=${this.limit}&intent=${this.intent}&section_id=upsell-data`;

      const [res, sectionRes] = await Promise.all([
        fetch(recsUrl, { credentials: 'same-origin' }),
        fetch(sectionUrl, { credentials: 'same-origin' }).catch(() => null),
      ]);

      if (!res.ok) {
        this.remove();
        return;
      }
      const data = await res.json();

      // Parse short titles map from the upsell-data section response.
      this.shortTitleMap = {};
      if (sectionRes && sectionRes.ok) {
        try {
          const html = await sectionRes.text();
          const match = html.match(/<script[^>]*data-upsell-meta[^>]*>([\s\S]*?)<\/script>/);
          if (match) {
            JSON.parse(match[1]).forEach(({ handle, short_title }) => {
              if (handle && short_title) this.shortTitleMap[handle] = short_title;
            });
          }
        } catch (e) {
          /* fall back to product.title */
        }
      }

      const products = (data.products || []).slice(0, this.limit);

      if (products.length === 0) {
        this.remove();
        return;
      }

      this.render(products);
    } catch (err) {
      console.warn('[atb-pdp-upsell] load failed:', err);
      this.remove();
    }
  }

  render(products) {
    this.grid.innerHTML = '';

    products.forEach((product) => {
      const card = this.template.content.firstElementChild.cloneNode(true);
      card.dataset.productId = product.id;

      const productUrl = product.url || `${this.routeRoot}products/${product.handle}`;
      const mediaLink = card.querySelector('.atb-pdp-upsell__media');
      const img = card.querySelector('.atb-pdp-upsell__image');
      const titleLink = card.querySelector('.atb-pdp-upsell__title');
      const priceEl = card.querySelector('.atb-pdp-upsell__price');
      const addBtn = card.querySelector('.atb-pdp-upsell__add');
      const viewLink = card.querySelector('.atb-pdp-upsell__view');

      // Links
      mediaLink.href = productUrl;
      mediaLink.setAttribute('aria-hidden', 'false');
      mediaLink.removeAttribute('tabindex');
      mediaLink.setAttribute('aria-label', product.title);
      titleLink.href = productUrl;
      titleLink.textContent =
        (this.shortTitleMap && this.shortTitleMap[product.handle]) || product.title;
      viewLink.href = productUrl;

      // Image
      const imgSrc = this._extractImageSrc(product);
      if (imgSrc) {
        img.src = this._sizedImage(imgSrc, '600x600');
        img.alt =
          product.featured_image && product.featured_image.alt
            ? product.featured_image.alt
            : product.title;
      } else {
        img.remove();
      }

      // Price
      priceEl.textContent = this._formatMoney(product.price);

      // Variant logic — Quick Add only when there's exactly one variant.
      const variants = product.variants || [];
      const availableVariants = variants.filter((v) => v.available);
      const isSingleVariant = variants.length === 1;

      if (availableVariants.length === 0) {
        // Fully sold out — show disabled state
        addBtn.disabled = true;
        addBtn.classList.add('atb-pdp-upsell__add--disabled');
        addBtn.querySelector('.atb-pdp-upsell__add-text').textContent = 'Sold out';
        viewLink.hidden = true;
      } else if (isSingleVariant) {
        // Single variant available → quick add
        const variantId = availableVariants[0].id;
        addBtn.dataset.variantId = variantId;
        addBtn.addEventListener('click', () => this.quickAdd(variantId, addBtn));
        viewLink.hidden = true;
      } else {
        // Multiple variants → push to PDP
        addBtn.hidden = true;
        viewLink.hidden = false;
      }

      this.grid.appendChild(card);
    });
  }

  async quickAdd(variantId, button) {
    const textSpan = button.querySelector('.atb-pdp-upsell__add-text');
    const loadingSpan = button.querySelector('.atb-pdp-upsell__add-loading');

    button.disabled = true;
    if (textSpan) textSpan.hidden = true;
    if (loadingSpan) loadingSpan.hidden = false;

    try {
      const sections = this._sectionsToRender();
      const body = JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: sections.map((s) => s.section).join(','),
        sections_url: window.location.pathname,
      });

      const res = await fetch(`${this.routeRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body,
      });
      const data = await res.json();

      if (data.status) {
        // 422 / error response
        console.warn('[atb-pdp-upsell] add error:', data.description || data.message);
        return;
      }

      this._updateCartDrawer(data);
      this._publishCartUpdate(variantId, data);

      // Do NOT call cartDrawer.open() here. Dawn's renderContents()
      // already schedules .open() via setTimeout (cart-drawer.js ~line 86).
      // Calling open() synchronously a second time adds the 'animate' +
      // 'active' classes in the same tick as the renderContents setTimeout,
      // which skips the slide-in transition. Matches Dawn's own
      // product-form.js behavior (publish → renderContents, no open).
    } catch (err) {
      console.warn('[atb-pdp-upsell] quick add failed:', err);
    } finally {
      button.disabled = false;
      if (textSpan) textSpan.hidden = false;
      if (loadingSpan) loadingSpan.hidden = true;
    }
  }

  // --- helpers ------------------------------------------------------------

  _updateCartDrawer(parsedState) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      // Dawn's renderContents only strips `is-empty` from `.drawer__inner` —
      // it leaves the outer <cart-drawer class="drawer is-empty"> alone.
      // That stale class triggers the empty-cart grid layout (1fr row)
      // which collapses the line-items area to a blank strip after the
      // first add from an empty cart. Toggle it ourselves to match the
      // actual item_count.
      if (typeof parsedState.item_count === 'number') {
        cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
      } else {
        cartDrawer.classList.remove('is-empty');
      }
      cartDrawer.renderContents(parsedState);
      return;
    }

    // Manual fallback: replace section HTML using returned sections.
    this._sectionsToRender().forEach((section) => {
      const html = parsedState.sections && parsedState.sections[section.section];
      if (!html) return;
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!target) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector(section.selector || '.shopify-section');
      if (replacement) target.innerHTML = replacement.innerHTML;
    });
  }

  _publishCartUpdate(variantId, cartData) {
    if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'atb-pdp-upsell',
        productVariantId: String(variantId),
        cartData,
      });
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cartData }));
    }
  }

  _sectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  _extractImageSrc(product) {
    if (!product) return null;
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && (product.featured_image.url || product.featured_image.src)) {
      return product.featured_image.url || product.featured_image.src;
    }
    if (Array.isArray(product.images) && product.images.length) {
      const first = product.images[0];
      return typeof first === 'string' ? first : first.src || first.url;
    }
    return null;
  }

  _sizedImage(src, size) {
    if (!src) return '';
    let url = src.startsWith('//') ? `https:${src}` : src;
    url = url.replace(/(\.(?:jpe?g|png|gif|webp|avif))(\?|$)/i, `_${size}$1$2`);
    return url;
  }

  _formatMoney(price) {
    const cents = typeof price === 'string' ? parseInt(price, 10) : price;
    if (!Number.isFinite(cents)) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (_) {
        /* fall through */
      }
    }

    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('atb-pdp-upsell')) {
  customElements.define('atb-pdp-upsell', AtbPdpUpsell);
}
